import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useBranch, useShopInfo } from '../../settings/SettingsContext';
import SocialButtons from '../SocialButtons';
import OpenStatus from './OpenStatus';
import { ContactRows, HoursTable } from './BranchDetails';
import './branch.css';
import { displayName } from '../../branch/name';

interface BranchInfoSheetProps {
  open: boolean;
  onClose: () => void;
  lang: 'gr' | 'en';
  now: Date;
}

/**
 * Everything about the branch in one bottom sheet. A native modal <dialog> (focus trap, Esc,
 * top layer) labelled by its heading. Wi-Fi, review, loyalty, delivery and socials come from
 * the existing SocialButtons/DeliveryButtons.
 */
const BranchInfoSheet: React.FC<BranchInfoSheetProps> = ({ open, onClose, lang, now }) => {
  const ref = useRef<HTMLDialogElement>(null);
  const branch = useBranch();
  const info = useShopInfo();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } else if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
    if (!open) return;
    // ponytail: body overflow lock; iOS Safari can still rubber-band the page behind the sheet.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!branch) return null;

  return (
    <dialog
      ref={ref}
      className="info-sheet"
      aria-labelledby="info-sheet-title"
      onClose={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault(); // one close path, so React state and the dialog never disagree
          onClose();
        }
      }}
      onClick={(e) => {
        // the dialog box itself is only hit outside the inner panel, i.e. on the backdrop
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="info-sheet__inner">
        <div className="info-sheet__grab" aria-hidden="true" />
        <div className="info-sheet__head">
          <h2 id="info-sheet-title">{displayName(branch, lang)}</h2>
          <button type="button" className="info-sheet__close" onClick={onClose} aria-label={lang === 'en' ? 'Close' : 'Κλείσιμο'}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <OpenStatus hours={branch.hours} now={now} lang={lang} />
        <ContactRows info={info} lang={lang} />
        {info.infoText && <p className="info-sheet__text">{info.infoText}</p>}
        <HoursTable hours={branch.hours} lang={lang} now={now} />
        <div className="info-sheet__extras">
          <SocialButtons selectedLanguage={lang} showContact={false} />
        </div>
      </div>
    </dialog>
  );
};

export default BranchInfoSheet;
